import React, { useContext, useEffect, useRef, useState } from "react";

import { useMutation } from "@apollo/client";
import { sendCode, sendCodeVariables } from "@layout/modals/graphql/__generated__/sendCode";
import { SEND_CODE } from "@layout/modals/graphql/SEND_CODE";
import { FormTypeEnum, LoginChangeForm, LoginTypeEnum } from "@layout/modals/types";
import { getErrorByCode, setAuthParamToURL } from "@layout/modals/utils";
import { useLocation } from "@reach/router";
import _uniqueId from "lodash/uniqueId";

import PhoneInput from "@/components/common/Inputs/phoneInput";
import { ErrorCodeEnum } from "@/constants";
import AuthorizationFormContext from "@/contexts/AuthorizationForm/AuthorizationFormContext";
import { useSmartCaptcha } from "@/contexts/SmartCaptcha/useSmartCaptcha";
import UserContext from "@/contexts/User/UserContext";
import { useManageUtmData } from "@/hooks/useManageUtmData";
import { getErrorCode } from "@/utils/apolloUtils";
import { Button, ButtonColorEnum, ButtonSizeEnum } from "@components/common/button";
import { SEND_OTP_CODE_RETRY_DELAY } from "@components/constants";

const LoginByPhoneModal = (props: LoginChangeForm) => {
  const { clientReferrer } = useContext(UserContext);
  const { setIsFirstLoginCallback } = props;
  const [sendCodeMutation, { data, loading, error }] = useMutation<sendCode, sendCodeVariables>(SEND_CODE, {
    fetchPolicy: "network-only",
  });
  const location = useLocation();
  const { executeCaptcha, prepareCaptcha } = useSmartCaptcha();

  // Капча монтируется только когда нужна (SEO #12): подгружаем её при открытии формы,
  // чтобы к отправке внешний скрипт уже был готов и пользователь не ждал.
  useEffect(() => {
    prepareCaptcha();
  }, [prepareCaptcha]);

  const [phone, setPhone] = useState("");
  const [phoneErrorText, setPhoneErrorText] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const { sentCodeTimestamp, setSentCodeTimestamp, setSentContact } = useContext(AuthorizationFormContext);
  const { getUtmData } = useManageUtmData();
  const [inputUniqueId] = useState(_uniqueId());
  // Телефон из последнего запроса sendCode; попадает в контекст, когда бэк подтвердил отправку кода
  const requestedPhoneRef = useRef("");

  const handleSubmit = async () => {
    if (phoneErrorText) {
      return;
    }

    const yandexCaptchaToken = await executeCaptcha();
    if (!yandexCaptchaToken) {
      setErrorCode(ErrorCodeEnum.StatusCodeReCaptchaError);
    } else {
      requestedPhoneRef.current = phone;
      sendCodeMutation({
        variables: {
          phone,
          utm: getUtmData(),
          reCaptcha: yandexCaptchaToken,
          referer: clientReferrer,
        },
      });
    }
  };

  useEffect(() => {
    if (data && !loading && !error) {
      setErrorCode(null);
      setSentCodeTimestamp(Date.now() + SEND_OTP_CODE_RETRY_DELAY);
      setSentContact({ loginType: LoginTypeEnum.LoginByPhone, value: requestedPhoneRef.current });
      if (setIsFirstLoginCallback) {
        setIsFirstLoginCallback(!!data.sendCode);
      }
      setAuthParamToURL(location, FormTypeEnum.CodeFromSMS);
    }
  }, [data, loading, error, setSentCodeTimestamp, setSentContact, setIsFirstLoginCallback, location]);

  useEffect(() => {
    if (error) {
      // If we get the StatusCodeTooManyRequests provide user to form with code
      if (getErrorCode(error) === ErrorCodeEnum.StatusCodeTooManyRequests) {
        if (!sentCodeTimestamp) {
          setSentCodeTimestamp(Date.now() + SEND_OTP_CODE_RETRY_DELAY);
        }
        setSentContact({ loginType: LoginTypeEnum.LoginByPhone, value: requestedPhoneRef.current });
        if (setIsFirstLoginCallback) {
          setIsFirstLoginCallback(!!data?.sendCode);
        }
        setAuthParamToURL(location, FormTypeEnum.CodeFromSMS);
      } else {
        setErrorCode(getErrorCode(error));
      }
    }
  }, [error, data, location, sentCodeTimestamp, setSentCodeTimestamp, setSentContact, setIsFirstLoginCallback]);

  const onPhoneChanged = (changedPhone: string, changedErrorText: string | null) => {
    setPhone(changedPhone);
    setPhoneErrorText(changedErrorText);
  };
  return (
    <>
      <div className="input-group">
        <div className="input-group__item">
          <PhoneInput
            id={inputUniqueId}
            phoneInitial={phone}
            onChange={onPhoneChanged}
            onSubmit={handleSubmit}
          />
        </div>
        {phone && phoneErrorText !== null && (
          <div className="input-group__error">{phoneErrorText}</div>
        )}
        {errorCode && <div className="input-group__error">{getErrorByCode(errorCode)}</div>}
      </div>
      <Button
        text="Получить код в СМС"
        isLoading={loading}
        size={ButtonSizeEnum.Large}
        color={ButtonColorEnum.Dark}
        onClick={handleSubmit}
        disabled={!phone || !!phoneErrorText || loading}
      />
      <div className="auth__license">
        <div>
          Нажимая на&nbsp;кнопку «Получить код в&nbsp;СМС»,
          <br />
          даю
          {" "}
          <a href="/account-agree.pdf" target="_blank" rel="noreferrer">
            согласие
          </a>
          {" "}
          на обработку моих персональных данных
          {" "}
          <br />
          в соответствии с
          {" "}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">
            Политикой конфиденциальности
          </a>
        </div>
      </div>
      <div className="auth__text">
        <b onClick={() =>
          setAuthParamToURL(location, FormTypeEnum.LoginAsExpert)}
        >
          Войти как эксперт
        </b>
      </div>
    </>
  );
};

export default LoginByPhoneModal;
